/**
 * Shared analytics event name constants and property types.
 *
 * Both bullrun-web and bullrun-mobile import from here so that PostHog
 * funnels, dashboards, and cohort definitions use identical event names
 * across platforms.
 */
// =============================================================================
// EVENT NAMES
// =============================================================================
export const AppEvents = {
    APP_OPENED: "app_opened",
    APP_BACKGROUNDED: "app_backgrounded",
    APP_FOREGROUNDED: "app_foregrounded",
    SESSION_STARTED: "session_started",
};
export const AuthEvents = {
    LOGIN_SCREEN_VIEWED: "auth_login_screen_viewed",
    AUTH_SHEET_OPENED: "auth_sheet_opened",
    AUTH_SHEET_CLOSED: "auth_sheet_closed",
    AUTH_METHOD_SELECTED: "auth_method_selected",
    EMAIL_OTP_REQUESTED: "auth_email_otp_requested",
    EMAIL_OTP_SENT: "auth_email_otp_sent",
    EMAIL_OTP_SEND_FAILED: "auth_email_otp_send_failed",
    EMAIL_OTP_VERIFIED: "auth_email_otp_verified",
    EMAIL_OTP_VERIFY_FAILED: "auth_email_otp_verify_failed",
    PHONE_OTP_REQUESTED: "auth_phone_otp_requested",
    PHONE_OTP_SENT: "auth_phone_otp_sent",
    PHONE_OTP_SEND_FAILED: "auth_phone_otp_send_failed",
    PHONE_OTP_VERIFIED: "auth_phone_otp_verified",
    PHONE_OTP_VERIFY_FAILED: "auth_phone_otp_verify_failed",
    OAUTH_INITIATED: "auth_oauth_initiated",
    OAUTH_SUCCESS: "auth_oauth_success",
    OAUTH_FAILED: "auth_oauth_failed",
    WALLET_IMPORT_STARTED: "auth_wallet_import_started",
    WALLET_IMPORT_SUCCESS: "auth_wallet_import_success",
    WALLET_IMPORT_FAILED: "auth_wallet_import_failed",
    LOGIN_SUCCESS: "auth_login_success",
    LOGIN_FAILED: "auth_login_failed",
    SIGNUP_SUCCESS: "auth_signup_success",
    SIGNUP_FAILED: "auth_signup_failed",
    LOGOUT: "auth_logout",
    SECURITY_CHECK_BLOCKED: "auth_security_check_blocked",
    RATE_LIMIT_HIT: "auth_rate_limit_hit",
};
export const OnboardingEvents = {
    WAITLIST_SCREEN_VIEWED: "onboarding_waitlist_viewed",
    WAITLIST_CODE_ENTERED: "onboarding_waitlist_code_entered",
    WAITLIST_CODE_VALIDATED: "onboarding_waitlist_code_validated",
    WAITLIST_CODE_INVALID: "onboarding_waitlist_code_invalid",
    ONBOARDING_STARTED: "onboarding_started",
    ONBOARDING_STEP_VIEWED: "onboarding_step_viewed",
    ONBOARDING_COMPLETED: "onboarding_completed",
    ONBOARDING_SKIPPED: "onboarding_skipped",
};
export const NavigationEvents = {
    SCREEN_VIEW: "nav_screen_view",
    TAB_CHANGED: "nav_tab_changed",
    DEEP_LINK_OPENED: "nav_deep_link_opened",
};
export const TradingEvents = {
    TOKEN_VIEWED: "trading_token_viewed",
    TOKEN_TRADE_STARTED: "trading_token_trade_started",
    TOKEN_TRADE_SUBMITTED: "trading_token_trade_submitted",
    TOKEN_TRADE_SUCCESS: "trading_token_trade_success",
    TOKEN_TRADE_FAILED: "trading_token_trade_failed",
    PERP_VIEWED: "trading_perp_viewed",
    PERP_TRADE_STARTED: "trading_perp_trade_started",
    PERP_TRADE_SUBMITTED: "trading_perp_trade_submitted",
    PERP_TRADE_SUCCESS: "trading_perp_trade_success",
    PERP_TRADE_FAILED: "trading_perp_trade_failed",
    PREDICTION_VIEWED: "trading_prediction_viewed",
    PREDICTION_TRADE_STARTED: "trading_prediction_trade_started",
    PREDICTION_TRADE_SUBMITTED: "trading_prediction_trade_submitted",
    PREDICTION_TRADE_SUCCESS: "trading_prediction_trade_success",
    PREDICTION_TRADE_FAILED: "trading_prediction_trade_failed",
};
export const WalletEvents = {
    DEPOSIT_STARTED: "wallet_deposit_started",
    DEPOSIT_ADDRESS_COPIED: "wallet_deposit_address_copied",
    DEPOSIT_COMPLETED: "wallet_deposit_completed",
    WITHDRAW_STARTED: "wallet_withdraw_started",
    WITHDRAW_SUBMITTED: "wallet_withdraw_submitted",
    WITHDRAW_SUCCESS: "wallet_withdraw_success",
    WITHDRAW_FAILED: "wallet_withdraw_failed",
    SEND_STARTED: "wallet_send_started",
    SEND_SUBMITTED: "wallet_send_submitted",
    SEND_SUCCESS: "wallet_send_success",
    SEND_FAILED: "wallet_send_failed",
    RECEIVE_VIEWED: "wallet_receive_viewed",
};
export const CexEvents = {
    CONNECTION_STARTED: "cex_connection_started",
    MESH_LINK_OPENED: "cex_mesh_link_opened",
    MESH_LINK_CLOSED: "cex_mesh_link_closed",
    CONNECTION_SUCCESS: "cex_connection_success",
    CONNECTION_FAILED: "cex_connection_failed",
    EMAIL_FOUND: "cex_email_found",
    EMAIL_NOT_FOUND: "cex_email_not_found",
    EMAIL_MANUAL_ENTRY: "cex_email_manual_entry",
    EMAIL_OTP_REQUESTED: "cex_email_otp_requested",
    EMAIL_OTP_VERIFIED: "cex_email_otp_verified",
    EMAIL_OTP_FAILED: "cex_email_otp_failed",
    ACCOUNT_LINKED: "cex_account_linked",
    ACCOUNT_UNLINKED: "cex_account_unlinked",
};
export const SearchEvents = {
    SEARCH_OPENED: "search_opened",
    SEARCH_QUERY: "search_query",
    SEARCH_RESULT_TAPPED: "search_result_tapped",
    SEARCH_FILTER_CHANGED: "search_filter_changed",
};
export const EngagementEvents = {
    REFERRAL_SHARED: "engagement_referral_shared",
    SOCIAL_LINK_TAPPED: "engagement_social_link_tapped",
    FOLLOW_TOGGLED: "engagement_follow_toggled",
    CHART_TIMEFRAME_CHANGED: "engagement_chart_timeframe_changed",
    WATCHLIST_TOGGLED: "engagement_watchlist_toggled",
    INSTRUMENT_SHARED: "engagement_instrument_shared",
};
export const SettingsEvents = {
    SETTINGS_OPENED: "settings_opened",
    WALLET_CONNECTED: "settings_wallet_connected",
    WALLET_DISCONNECTED: "settings_wallet_disconnected",
    PREFERENCE_CHANGED: "settings_preference_changed",
};
export const ErrorEvents = {
    API_ERROR: "error_api",
    NETWORK_ERROR: "error_network",
    WEBSOCKET_ERROR: "error_websocket",
    UNEXPECTED_ERROR: "error_unexpected",
};
// =============================================================================
// HELPERS
// =============================================================================
export function maskEmail(email) {
    const [local, domain] = email.split("@");
    if (!local || !domain)
        return "***@***";
    return `${local[0]}***@${domain}`;
}
export function maskPhone(phone) {
    if (phone.length < 6)
        return "***";
    return `${phone.slice(0, 3)}***${phone.slice(-4)}`;
}
export function createAuthTimer() {
    let startTime = 0;
    return {
        start: () => {
            startTime = Date.now();
        },
        elapsed: () => {
            return startTime ? Date.now() - startTime : 0;
        },
    };
}
